import { Helmet } from 'react-helmet-async';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  keywords?: string;
  jsonLd?: Record<string, unknown>;
  jsonLdExtra?: Record<string, unknown>[];
  breadcrumbs?: BreadcrumbItem[];
}

// Search snippets must never contain em/en dashes - normalise every value we emit.
const DASH_RE = /\s*[\u2014\u2013]\s*/g;

function noDash(value: string): string {
  return value.replace(DASH_RE, ' - ').replace(/\s{2,}/g, ' ').trim();
}

function deepNoDash<T>(value: T): T {
  if (typeof value === 'string') return noDash(value) as unknown as T;
  if (Array.isArray(value)) return value.map(deepNoDash) as unknown as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, deepNoDash(v)])
    ) as unknown as T;
  }
  return value;
}

export default function SEOHead({ title: rawTitle, description: rawDescription, canonical, type = 'website', keywords: rawKeywords, jsonLd: rawJsonLd, jsonLdExtra: rawJsonLdExtra, breadcrumbs: rawBreadcrumbs }: SEOHeadProps) {
  const title = noDash(rawTitle);
  const description = noDash(rawDescription);
  const keywords = rawKeywords ? noDash(rawKeywords) : rawKeywords;
  const jsonLd = rawJsonLd ? deepNoDash(rawJsonLd) : rawJsonLd;
  const jsonLdExtra = rawJsonLdExtra ? deepNoDash(rawJsonLdExtra) : rawJsonLdExtra;
  const breadcrumbs = rawBreadcrumbs ? deepNoDash(rawBreadcrumbs) : rawBreadcrumbs;
  const breadcrumbJsonLd = breadcrumbs && breadcrumbs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      }
    : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      )}
      {jsonLdExtra?.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
